import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";

// @mui material components
import Card from "@mui/material/Card";
import Switch from "@mui/material/Switch";
import CircularProgress from "@mui/material/CircularProgress";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDSnackbar from "components/MDSnackbar";

// Authentication layout components
import BasicLayout from "layouts/authentication/components/BasicLayout";

// Images
import bgImage from "assets/images/bg-sign-in-basic.jpeg";

import httpClient from "api/httpClient";
import { useTranslation } from "react-i18next";

function Basic() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [rememberMe, setRememberMe] = useState(false);
  const [scCode, setScCode] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const [scCodeError, setScCodeError] = useState(false);
  const [emailError, setEmailError] = useState(false);
  const [passwordError, setPasswordError] = useState(false);

  const [loading, setLoading] = useState(false);
  const [errorSB, setErrorSB] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const openErrorSB = () => setErrorSB(true);
  const closeErrorSB = () => setErrorSB(false);
  const handleSetRememberMe = () => setRememberMe(!rememberMe);

  const renderErrorSB = (
    <MDSnackbar
      color="error"
      icon="warning"
      title={t("auth.signInErrorTitle")}
      content={errorMessage || ""}
      dateTime=""
      open={errorSB}
      onClose={closeErrorSB}
      close={closeErrorSB}
      bgWhite
    />
  );

  const handleKeyUp = (event) => {
    if (event.key === "Enter") {
      handleLogin();
    }
  };

  const validateForm = () => {
    let valid = true;

    if (!scCode) {
      setScCodeError(true);
      valid = false;
    } else {
      setScCodeError(false);
    }

    if (!email) {
      setEmailError(true);
      valid = false;
    } else {
      setEmailError(false);
    }

    if (!password) {
      setPasswordError(true);
      valid = false;
    } else {
      setPasswordError(false);
    }

    return valid;
  };

  const handleLogin = async () => {
    if (!validateForm()) return;

    setLoading(true);

    try {
      const response = await httpClient.post(
        "/api/auth/login",
        { scCode, email, password },
        { withCredentials: true }
      );

      localStorage.setItem("auth", JSON.stringify(response.data));

      if (response.data.passwordResetRequired) {
        navigate("/authentication/reset-password", { replace: true });
        return;
      }

      navigate("/dashboard", { replace: true });
    } catch (err) {
      if (err.response && err.response.status === 401) {
        setErrorMessage(t("auth.invalidCredentials"));
        openErrorSB();
        return;
      }
      setErrorMessage(err.response?.data?.message || err.message || "An error occurred");
      openErrorSB();
    } finally {
      setLoading(false);
    }
  };

  return (
    <BasicLayout image={bgImage}>
      <Card>
        <MDBox
          variant="gradient"
          bgColor="info"
          borderRadius="lg"
          coloredShadow="info"
          mx={2}
          mt={-3}
          p={2}
          mb={1}
          textAlign="center"
        >
          <MDTypography variant="h4" fontWeight="medium" color="white" mt={1}>
            {t("auth.signInTitle")}
          </MDTypography>
        </MDBox>
        <MDBox pt={4} pb={3} px={3}>
          <MDBox component="form" role="form">
            <MDBox mb={2}>
              <MDInput
                type="text"
                label={t("auth.scCodeLabel") || "Service Center Code"}
                fullWidth
                error={scCodeError}
                value={scCode}
                onChange={(e) => setScCode(e.target.value.toUpperCase())}
                onKeyUp={handleKeyUp}
              />
            </MDBox>
            <MDBox mb={2}>
              <MDInput
                type="email"
                label={t("auth.emailLabel")}
                fullWidth
                error={emailError}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyUp={handleKeyUp}
              />
            </MDBox>
            <MDBox mb={2}>
              <MDInput
                type="password"
                label={t("auth.passwordLabel")}
                fullWidth
                error={passwordError}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyUp={handleKeyUp}
              />
            </MDBox>
            <MDBox display="flex" alignItems="center" ml={-1}>
              <Switch checked={rememberMe} onChange={handleSetRememberMe} />
              <MDTypography
                variant="button"
                fontWeight="regular"
                color="text"
                onClick={handleSetRememberMe}
                sx={{ cursor: "pointer", userSelect: "none", ml: -1 }}
              >
                &nbsp;&nbsp;Remember me
              </MDTypography>
            </MDBox>
            <MDBox mt={4} mb={1}>
              <MDButton
                onClick={handleLogin}
                variant="gradient"
                color="info"
                fullWidth
                disabled={loading}
                sx={{ height: "45px" }}
              >
                {loading ? <CircularProgress size={24} color="white" /> : t("auth.signInButton")}
              </MDButton>
              {renderErrorSB}
            </MDBox>
            {/*<MDBox mt={3} mb={1} textAlign="center">
              <MDTypography variant="button" color="text">
                {t("auth.forgotPassword")}{" "}
                <MDTypography
                  component={Link}
                  to="/authentication/sign-up"
                  variant="button"
                  color="info"
                  fontWeight="medium"
                  textGradient
                >
                  {t("auth.resetIt")}
                </MDTypography>
              </MDTypography>
            </MDBox>
            */}
          </MDBox>
        </MDBox>
      </Card>
    </BasicLayout>
  );
}

export default Basic;
